import { useEffect, useRef, useState } from 'react';
import type { ServiceLandingData } from '../../../mocks/service-landings';

interface SLFaqProps {
  data: ServiceLandingData;
}

function useInView() {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInView(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  return { ref, inView };
}

export default function SLFaq({ data }: SLFaqProps) {
  const { ref, inView } = useInView();
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  return (
    <section className="py-24 bg-white">
      <div className="max-w-3xl mx-auto px-6">
        <div ref={ref}>
          {/* Header */}
          <div
            className={`text-center mb-12 transition-all duration-700 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
          >
            <div className="inline-flex items-center gap-2 bg-zinc-50 border border-zinc-200 rounded-full px-4 py-1.5 mb-5">
              <i className="ri-question-answer-line text-zinc-500 text-sm" />
              <span className="text-zinc-600 text-sm font-semibold">Časté otázky</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-black text-zinc-900 leading-tight">
              {data.faq.heading}
            </h2>
          </div>

          {/* FAQ items */}
          <div
            className={`divide-y divide-zinc-100 transition-all duration-700 delay-200 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
          >
            {data.faq.items.map((item, i) => (
              <div key={item.q} className="py-5">
                <button
                  className="w-full flex items-start justify-between gap-4 text-left cursor-pointer group"
                  onClick={() => setOpenIndex(openIndex === i ? null : i)}
                >
                  <span className={`font-semibold text-base leading-snug transition-colors duration-200 ${openIndex === i ? 'text-orange-500' : 'text-zinc-900 group-hover:text-zinc-700'}`}>
                    {item.q}
                  </span>
                  <div className={`flex-shrink-0 w-7 h-7 flex items-center justify-center rounded-full border transition-all duration-200 ${openIndex === i ? 'bg-orange-500 border-orange-500' : 'bg-white border-zinc-200 group-hover:border-zinc-300'}`}>
                    <i className={`text-sm transition-transform duration-200 ${openIndex === i ? 'ri-subtract-line text-white' : 'ri-add-line text-zinc-500'}`} />
                  </div>
                </button>
                <div
                  className={`overflow-hidden transition-all duration-300 ${openIndex === i ? 'max-h-60 opacity-100 mt-3' : 'max-h-0 opacity-0'}`}
                >
                  <p className="text-zinc-500 text-sm leading-relaxed pr-10">{item.a}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
